/*======================================================================
MAISON PRODUCT — PDP V1

Controlador ligero, vanilla JS, sin dependencias externas. Reutiliza
el sistema nativo de Shopify siempre que existe (Add to Cart vía
<product-form> ya definido en product-form.js — AJAX + integración
con cart-drawer nativa, sin tocar; el paso de cantidad vía
<quantity-input> ya definido en global.js). Este archivo solo
resuelve lo que es exclusivo de esta PDP: sincronizar precio /
inventario / disponibilidad / galería con la variante (Presentación)
seleccionada, la Capa 2 ("Conoce más") y el toggle de favoritos.

No toca discover-essence-engine.js, dictionary, knowledge ni
data-loader — es un archivo completamente independiente de esos.

Version 1.0
======================================================================*/

document.addEventListener("DOMContentLoaded", () => {
  document
    .querySelectorAll("[data-maison-product]")
    .forEach(initMaisonProduct);
});

function initMaisonProduct(section) {
  const galleryImages = [
    ...section.querySelectorAll("[data-gallery-image]"),
  ];

  const galleryThumbs = [
    ...section.querySelectorAll("[data-gallery-thumb]"),
  ];

  const radios = [...section.querySelectorAll("[data-variant-radio]")];

  const variantIdInput = section.querySelector("[data-variant-id]");

  const priceWrap = section.querySelector("[data-product-price]");

  const priceRegular = section.querySelector("[data-price-regular]");

  const quantityWrapper = section.querySelector("quantity-input");

  const quantityInput = section.querySelector("[data-quantity-input]");

  const submitBtn = section.querySelector("[data-add-to-cart]");

  const submitLabel = submitBtn ? submitBtn.querySelector("span") : null;

  const layer2Toggle = section.querySelector("[data-toggle-layer2]");

  const layer2 = section.querySelector("[data-layer2]");

  const favoriteBtn = section.querySelector("[data-favorite-toggle]");

  /*------------------------------------------------------
    Gallery — presentation-aware.

    Cada imagen/thumbnail trae data-option-value con el alt real de
    Shopify (verificado en el dataset real: coincide exactamente con
    variant.option1 en cada producto multi-presentación). Si ninguna
    imagen del producto coincide con NINGUNA presentación real
    (producto de una sola variante, sin alt-tagging real), se
    muestran todas — nunca se oculta contenido real por no encontrar
    coincidencia.

    FASE PDP-07 — caso distinto detectado en QA (YSL Libre Intense):
    el producto SÍ usa el sistema de tagging (sus imágenes traen
    alt="50ml"), pero a la presentación "90ml" nunca se le subieron
    fotos en Shopify (0 imágenes con alt="90ml", variant.image null
    — confirmado vía Admin API, no es un typo de alt). Con la lógica
    anterior esto hacía fallback a "mostrar todas", mezclando fotos
    de 50ml bajo la presentación 90ml — viola la regla crítica de no
    mezclar imágenes entre presentaciones. galleryIsTagged distingue
    ambos casos: solo se usa el fallback "mostrar todas" cuando el
    producto no tiene NINGÚN tagging real; si lo tiene pero a esta
    variante le faltan fotos, la galería queda vacía (dato real
    faltante en Shopify, no se inventa ni se mezcla).
  ------------------------------------------------------*/

  const knownOptionValues = new Set(
    radios
      .map((radio) => {
        try {
          return JSON.parse(radio.dataset.variant).option1;
        } catch (error) {
          return null;
        }
      })
      .filter(Boolean),
  );

  const galleryIsTagged = galleryImages.some((img) =>
    knownOptionValues.has(img.dataset.optionValue),
  );

  function filterGallery(optionValue) {
    if (!galleryImages.length) return;

    const showAllAsFallback = !galleryIsTagged;

    let firstVisible = null;

    galleryImages.forEach((img) => {
      const show = showAllAsFallback || img.dataset.optionValue === optionValue;

      img.hidden = !show;

      img.classList.remove("is-active");

      if (show && !firstVisible) firstVisible = img;
    });

    if (firstVisible) firstVisible.classList.add("is-active");

    let firstThumb = null;

    galleryThumbs.forEach((thumb) => {
      const show = showAllAsFallback || thumb.dataset.optionValue === optionValue;

      thumb.hidden = !show;

      thumb.classList.remove("is-active");

      if (show && !firstThumb) firstThumb = thumb;
    });

    if (firstThumb) firstThumb.classList.add("is-active");

    startGalleryAutoplay();
  }

  /*------------------------------------------------------
    Slideshow — avance automático, editorial y silencioso (PDP VISUAL
    REFINEMENT PASS 01 + MICRO REFINEMENT). Recorre únicamente las
    imágenes visibles (respeta el filtrado por Presentación de
    filterGallery, arriba — nunca mezcla fotos de otra variante). Un
    solo setInterval compartido, reiniciado (no acumulado) en cada
    cambio de estado: selección manual de thumbnail, cambio de
    variante, o el propio avance automático. prefers-reduced-motion
    detiene el ciclo por completo — no solo acelera la transición —
    dejando una imagen estática con la interacción manual intacta.

    Crossfade simultáneo (MICRO REFINEMENT 02 — revierte el intento
    anterior de fade-out→pausa→fade-in secuencial: dejaba un frame sin
    ninguna imagen visible, se percibía como parpadeo, no como
    continuidad editorial). Ambas imágenes son position:absolute/inset:0
    sobre el mismo espacio (ya así desde la implementación original,
    sin cambios de stacking) — quitar .is-active de la saliente y
    agregarlo a la entrante EN LA MISMA pasada síncrona hace que ambas
    transiciones de opacity (--ms-motion-gallery-fade, --ms-ease-standard,
    ya definidas en maison-product.css) corran a la vez: A baja de 1 a 0
    mientras B sube de 0 a 1, nunca ambas en 0 al mismo tiempo. Bajo
    reduced motion el cambio sigue siendo instantáneo vía el catch-all
    global de 02_maison-base.css (transition-duration:.01ms).
  ------------------------------------------------------*/

  const GALLERY_DWELL = 7500;

  let galleryAutoplayTimer = null;

  function prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function getVisibleGalleryImages() {
    return galleryImages.filter((img) => !img.hidden);
  }

  function setActiveGalleryImage(image) {
    galleryImages.forEach((img) => img.classList.remove("is-active"));

    image.classList.add("is-active");

    const index = galleryImages.indexOf(image);

    galleryThumbs.forEach((thumb) => thumb.classList.remove("is-active"));

    const thumb = galleryThumbs.find(
      (t) => Number(t.dataset.index) === index,
    );

    if (thumb && !thumb.hidden) thumb.classList.add("is-active");
  }

  function transitionToGalleryImage(image) {
    if (!image || image.classList.contains("is-active")) return;

    setActiveGalleryImage(image);
  }

  function stopGalleryAutoplay() {
    if (galleryAutoplayTimer) {
      window.clearInterval(galleryAutoplayTimer);

      galleryAutoplayTimer = null;
    }
  }

  function startGalleryAutoplay() {
    stopGalleryAutoplay();

    if (prefersReducedMotion()) return;

    if (getVisibleGalleryImages().length < 2) return;

    galleryAutoplayTimer = window.setInterval(() => {
      const visible = getVisibleGalleryImages();

      if (visible.length < 2) return;

      const activeIndex = visible.findIndex((img) =>
        img.classList.contains("is-active"),
      );

      const nextIndex = (activeIndex + 1) % visible.length;

      transitionToGalleryImage(visible[nextIndex]);
    }, GALLERY_DWELL);
  }

  galleryThumbs.forEach((thumb) => {
    thumb.addEventListener("click", () => {
      const index = Number(thumb.dataset.index);

      const target = galleryImages[index];

      if (!target || target.hidden) return;

      transitionToGalleryImage(target);

      startGalleryAutoplay();
    });
  });

  /*------------------------------------------------------
    Variant sync — precio, inventario, disponibilidad, galería.

    NO cambia información olfativa (esa vive a nivel producto, fuera
    de este flujo) ni el título del perfume.
  ------------------------------------------------------*/

  function readVariant(radio) {
    try {
      return JSON.parse(radio.dataset.variant);
    } catch (error) {
      return null;
    }
  }

  function applyVariant(variant) {
    if (!variant) return;

    if (variantIdInput) variantIdInput.value = variant.id;

    if (priceRegular) priceRegular.textContent = variant.price;

    if (priceWrap) {
      let compareEl = priceWrap.querySelector("[data-price-compare]");

      if (variant.compare_at_price) {
        if (!compareEl) {
          compareEl = document.createElement("span");

          compareEl.className = "price-item price-item--sale";

          compareEl.setAttribute("data-price-compare", "");

          priceWrap.appendChild(compareEl);
        }

        compareEl.textContent = variant.compare_at_price;

        priceWrap.classList.add("price--on-sale");
      } else if (compareEl) {
        compareEl.remove();

        priceWrap.classList.remove("price--on-sale");
      }
    }

    // Inventario real de la variante — nunca una cantidad máxima fija.
    if (quantityInput) {
      const tracksInventory = variant.inventory_management === "shopify";

      const allowsOversell = variant.inventory_policy === "continue";

      if (tracksInventory && !allowsOversell) {
        const max = Math.max(variant.inventory_quantity, 0);

        quantityInput.max = String(max);

        if (Number(quantityInput.value) > max) {
          quantityInput.value = String(Math.max(max, 1));
        }
      } else {
        quantityInput.removeAttribute("max");
      }

      if (
        quantityWrapper &&
        typeof quantityWrapper.validateQtyRules === "function"
      ) {
        quantityWrapper.validateQtyRules();
      }
    }

    if (submitBtn) {
      const available = variant.available;

      submitBtn.toggleAttribute("aria-disabled", !available);

      submitBtn.disabled = !available;

      if (submitLabel) {
        submitLabel.textContent = available
          ? "Añadir al carrito"
          : "Agotado";
      }
    }

    filterGallery(variant.option1);
  }

  radios.forEach((radio) => {
    radio.addEventListener("change", () => {
      if (!radio.checked) return;

      applyVariant(readVariant(radio));
    });
  });

  const initialRadio = radios.find((radio) => radio.checked);

  if (initialRadio) {
    const initialVariant = readVariant(initialRadio);

    if (initialVariant) filterGallery(initialVariant.option1);
  }

  /*------------------------------------------------------
    Capa 2 — "Conoce más de esta fragancia"
  ------------------------------------------------------*/

  if (layer2Toggle && layer2) {
    layer2Toggle.addEventListener("click", () => {
      const isOpen = !layer2.hidden;

      layer2.hidden = isOpen;

      layer2Toggle.setAttribute("aria-expanded", String(!isOpen));

      if (!isOpen) {
        layer2.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  }

  /*------------------------------------------------------
    Favoritos — V1: persistencia local (localStorage), sin cuenta de
    cliente ni backend. Documentado como simplificación V1 en el
    reporte de cierre. Oct 2026: la lista se ve en /pages/guardados
    (sections/maison-favorites.liquid + assets/maison-favorites.js),
    misma clave "maisonFavorites".
  ------------------------------------------------------*/

  if (favoriteBtn) {
    const handle = favoriteBtn.dataset.productHandle;

    const storageKey = "maisonFavorites";

    const label = favoriteBtn.querySelector("[data-favorite-label]");

    const favoritesLink = section.querySelector("[data-favorite-link]");

    function readFavorites() {
      try {
        return JSON.parse(localStorage.getItem(storageKey)) || [];
      } catch (error) {
        return [];
      }
    }

    function writeFavorites(list) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(list));
      } catch (error) {
        // localStorage no disponible — no bloquea el resto de la UI.
      }
    }

    function isFavorite() {
      return readFavorites().includes(handle);
    }

    function render() {
      const active = isFavorite();

      favoriteBtn.classList.toggle("is-active", active);

      favoriteBtn.setAttribute("aria-pressed", String(active));

      if (label) label.textContent = active ? "Guardado" : "Guardar";

      if (favoritesLink) favoritesLink.hidden = !active;
    }

    favoriteBtn.addEventListener("click", () => {
      const list = readFavorites();

      const index = list.indexOf(handle);

      if (index > -1) {
        list.splice(index, 1);
      } else {
        list.push(handle);
      }

      writeFavorites(list);

      render();

      if (index === -1) {
        favoriteBtn.classList.remove("is-popping");
        void favoriteBtn.offsetWidth;
        favoriteBtn.classList.add("is-popping");
      }
    });

    favoriteBtn.addEventListener("animationend", () => {
      favoriteBtn.classList.remove("is-popping");
    });

    /* Si el cliente guarda/quita en otra pestaña (p. ej. /pages/guardados),
       el botón se actualiza solo. */
    window.addEventListener("storage", (event) => {
      if (event.key === storageKey) render();
    });

    render();
  }
}
